<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/storage'
import moment from 'moment-timezone'
import { amountInSatang, routeDeposit } from '../../utils/depositRouting'
import { normalizeBankCode } from '../../utils/receivingAccounts'

export default {
  name: 'DepositTransfer',
  props: {
    bank: { type: Object, required: true },
    user: { type: Object, required: true },
    session: { type: Number, default: 0 },
    ready: { type: Boolean, default: true },
    loadError: { type: String, default: '' }
  },
  data () {
    return { accountId: '', amount: '', selection: null, busy: false, progress: 0, error: '', submitted: false, slipId: '', slipStatus: '', scanStatus: '', stopStatus: null }
  },
  computed: {
    validAmount () { return amountInSatang(this.amount) !== null },
    bangkokScan () { return this.selection && this.selection.mode === 'custom_scan' && normalizeBankCode(this.user.bank) === 'bankokbank' },
    bankImage () {
      if (!this.selection || !this.selection.account) return ''
      try {
        if (this.selection.account.bankCode === 'clicx') return require('../../assets/images/bank/clicx.png')
        return require('../../assets/images/bank/' + this.selection.account.bankCode + '.jpg')
      } catch (error) {
        return ''
      }
    }
  },
  watch: {
    session () { if (!this.busy) this.reset() }
  },
  beforeDestroy () { if (this.stopStatus) this.stopStatus() },
  methods: {
    reset () {
      if (this.stopStatus) this.stopStatus()
      Object.assign(this, { amount: '', selection: null, error: '', submitted: false, slipId: '', slipStatus: '', scanStatus: '', stopStatus: null })
    },
    chooseAccount () {
      this.error = ''
      if (!this.ready) { this.error = this.loadError || 'กำลังโหลดบัญชีรับฝาก กรุณารอสักครู่'; return }
      const selection = routeDeposit(this.amount, this.bank, Math.random())
      if (!selection) { this.error = 'กรอกจำนวนเงินมากกว่า 0 บาท และทศนิยมไม่เกิน 2 ตำแหน่ง'; return }
      if (!selection.account) { this.error = 'ยังไม่มีบัญชีรับฝากที่พร้อมใช้งาน โปรดติดต่อแอดมิน'; return }
      this.selection = selection
      this.accountId = selection.account.accountId
    },
    changeAccount () {
      const account = (this.selection.accounts || []).find(a => a.accountId === this.accountId)
      if (!account || this.busy || this.submitted) return
      this.selection = { ...this.selection, account, requiresSlip: account.autoChannel === 'SCAN_SLIP', mode: account.autoChannel === 'SCAN_SLIP' ? 'custom_scan' : account.autoChannel === 'LINE' ? 'bank_line' : 'scb_sms' }
      this.slipId = ''; this.error = ''
    },
    updateSlipStatus (slip) {
      this.scanStatus = slip.status === 'กำลังดำเนินการ' ? slip.scan_status || '' : ''
      this.slipStatus = slip.status === 'ดำเนินการแล้ว' ? 'เติมเงินเรียบร้อยแล้ว' :
        slip.status === 'ยกเลิก' ? (slip.slip2go_error || 'รายการถูกยกเลิก กรุณาติดต่อแอดมิน') :
          slip.scan_status === 'processing' ? 'Processing — รอข้อมูลสลิปธนาคารกรุงเทพ 180 วินาที (3 นาที) แล้วสแกนอัตโนมัติ กรุณารอ' :
            slip.slip2go_error ? 'ส่งสลิปให้แอดมินตรวจสอบแล้ว' : 'ได้รับสลิปแล้ว กำลังตรวจสอบรายการ'
    },
    async upload (event) {
      const file = event.target.files && event.target.files[0]
      if (!file || this.busy || !this.selection || this.submitted) return
      this.error = ''
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        this.error = 'เลือกภาพ JPG, PNG หรือ WebP ขนาดไม่เกิน 10 MB'
        event.target.value = ''
        return
      }
      this.busy = true
      this.progress = 0
      const selection = this.selection
      try {
        if (!this.user.key) throw new Error('กรุณาเข้าสู่ระบบใหม่')
        // Create once and reuse on a failed upload. Image is attached only after Storage completes.
        const ref = this.slipId ? firebase.firestore().collection('slip').doc(this.slipId) : firebase.firestore().collection('slip').doc()
        this.slipId = ref.id
        const user = {}
        ;['code', 'firstname', 'lastname', 'bank', 'tel', 'account_no', 'bonus'].forEach(key => { user[key] = this.user[key] || '' })
        await ref.set({
          userkey: this.user.key, code: this.user.code || '', user,
          createdate: moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss'),
          status: 'รออัปโหลด', deposit_amount: selection.amount,
          deposit_mode: selection.mode, deposit_account: selection.account,
          depositSourceChannel: selection.account.autoChannel || (selection.mode === 'custom_scan' ? 'SCAN_SLIP' : 'SMS'),
          upload_status: 'uploading'
        }, { merge: true })
        const extension = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' }[file.type]
        const path = `images/slip/${ref.id}.${extension}`
        const task = firebase.storage().ref(path).put(file, { contentType: file.type })
        await new Promise((resolve, reject) => task.on('state_changed', snapshot => {
          this.progress = Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100)
        }, reject, resolve))
        const image = await task.snapshot.ref.getDownloadURL()
        await ref.update({ image, storage_path: path, status: 'กำลังดำเนินการ', upload_status: 'complete' })
        this.submitted = true
        this.stopStatus = ref.onSnapshot(doc => {
          this.updateSlipStatus(doc.data() || {})
        }, () => { this.scanStatus = ''; this.slipStatus = 'ส่งสลิปแล้ว ตรวจสอบผลได้จากประวัติฝากเงิน' })
      } catch (error) {
        this.error = 'ส่งสลิปไม่สำเร็จ กรุณาลองอีกครั้ง'
      } finally {
        this.busy = false
        event.target.value = ''
      }
    }
  }
}
</script>

<template>
  <section class="deposit-transfer">
    <form v-if="!selection" @submit.prevent="chooseAccount">
      <p v-if="!ready" :role="loadError ? 'alert' : 'status'">{{ loadError || 'กำลังโหลดบัญชีรับฝาก…' }}</p>
      <label for="transfer-amount">จำนวนเงินที่ต้องการฝาก (บาท)</label>
      <input id="transfer-amount" v-model.trim="amount" class="form-control text-center" inputmode="decimal" autocomplete="off" placeholder="กรอกจำนวนเงินก่อนเลือกบัญชี" required>
      <button type="submit" class="btn btn-primary mt-3" :disabled="!ready || !validAmount">แสดงบัญชีรับฝาก</button>
    </form>
    <template v-else>
      <p>ยอดฝาก <strong>{{ selection.amount.toLocaleString('th-TH', { minimumFractionDigits: 2 }) }}</strong> บาท</p>
      <label v-if="selection.accounts && selection.accounts.length > 1 && !submitted" class="receiving-choice">เลือกบัญชีที่ต้องการโอน
        <select v-model="accountId" class="form-control" :disabled="busy" @change="changeAccount"><option v-for="a in selection.accounts" :key="a.accountId" :value="a.accountId">{{ a.bankName }} · {{ a.accountNo }}</option></select>
      </label>
      <div class="deposit-account">
        <img v-if="bankImage" :src="bankImage" width="50" height="50" :alt="selection.account.bankName">
        <span v-else class="bank-fallback" aria-hidden="true">{{ selection.account.bankCode.toUpperCase() }}</span>
        <div>
          <div>{{ selection.account.bankName }}</div>
          <div>{{ selection.account.accountName }}</div>
          <strong class="account-number">{{ selection.account.accountNo }}</strong>
        </div>
      </div>
      <button v-clipboard:copy="selection.account.accountNo" type="button" class="btn btn-primary mt-2">คัดลอกเลขบัญชี</button>
      <p class="mt-3">โอนจากบัญชีที่ลงทะเบียน ให้ตรงกับยอดที่แจ้ง และตรวจสอบบัญชีรับฝากทุกครั้ง</p>
      <div v-if="selection.requiresSlip && !submitted" class="slip-required" role="note">
        <strong class="slip-required-title">โอนแล้ว ต้องแนบสลิป</strong>
        <p class="slip-required-deadline">กรุณาแนบสลิป <strong>ภายใน 5 นาทีหลังโอนเงิน</strong></p>
        <p class="slip-required-help">กดปุ่มด้านล่างเพื่อแนบสลิปและแจ้งฝากทุกครั้ง</p>
      </div>
      <p v-else-if="!selection.requiresSlip">ระบบตรวจสอบยอดโอนอัตโนมัติ หากเงินไม่เข้าภายใน 5 นาที สามารถแนบสลิปให้แอดมินตรวจสอบได้</p>
      <p v-if="bangkokScan && !submitted">โอนจากธนาคารกรุงเทพ: หลังส่งสลิป ระบบจะรอ 3 นาทีก่อนสแกนอัตโนมัติ</p>
      <template v-if="!submitted">
        <label class="upload-button btn btn-primary" :class="{ disabled: busy, 'upload-required': selection.requiresSlip }">
          {{ busy ? 'กำลังส่งสลิป…' : selection.requiresSlip ? 'อัปโหลดสลิปเพื่อแจ้งฝาก' : 'แนบสลิปให้แอดมินตรวจสอบ' }}
          <input class="slip-input" type="file" accept="image/jpeg,image/png,image/webp" :disabled="busy" @change="upload">
        </label>
        <div v-if="busy" role="status">{{ progress }}% — กรุณารอจนส่งสลิปสำเร็จ</div>
        <button v-if="!busy" type="button" class="btn btn-link d-block mx-auto" @click="reset">เปลี่ยนจำนวนเงิน</button>
      </template>
      <div v-else class="slip-result" :class="{ 'slip-processing': scanStatus === 'processing' }" role="status" aria-live="polite" :aria-busy="scanStatus === 'processing' ? 'true' : 'false'">
        {{ slipStatus || 'ส่งสลิปเรียบร้อยแล้ว' }}
        <button type="button" class="btn btn-link d-block mx-auto" @click="reset">ฝากรายการใหม่</button>
      </div>
    </template>
    <p v-if="error" class="deposit-error" role="alert">{{ error }}</p>
  </section>
</template>

<style scoped>
.deposit-transfer { padding: 12px 16px; text-align: center; font-family: 'Kanit', sans-serif; color: #fff; }
.deposit-transfer label { display: block; margin-bottom: 10px; }
.deposit-transfer .form-control { height: 48px; font-size: 18px; }
.deposit-transfer .btn-primary { background: #edb848; color: #1b1711; border-color: #edb848; }
.deposit-account { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 16px; background: rgba(255,255,255,.1); border-radius: 12px; }
.deposit-account img { border-radius: 50%; }
.bank-fallback { display: flex; align-items: center; justify-content: center; width: 50px; height: 50px; flex-shrink: 0; border-radius: 50%; background: #edb848; color: #1b1711; font-size: 11px; font-weight: 600; }
.account-number { font-size: 22px; overflow-wrap: anywhere; }
.slip-required { margin: 20px 0 14px; padding: 18px 14px; background: #711f29; border: 2px solid #ffb3bc; border-radius: 12px; }
.slip-required-title { display: block; color: #fff; font-size: 20px; line-height: 1.4; }
.slip-required-deadline { margin: 10px 0; color: #fff; font-size: 18px; line-height: 1.5; }
.slip-required-deadline strong { display: block; color: #ffe080; font-size: 24px; }
.slip-required-help { margin: 0; color: #fff; font-size: 14px; line-height: 1.5; }
.slip-input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.upload-button { cursor: pointer; white-space: normal; }
.deposit-transfer .upload-required { padding: 14px 12px; border-radius: 8px; font-size: 18px; font-weight: 600; }
.upload-button:focus-within { outline: 3px solid #fff; }
.deposit-error { margin-top: 16px; color: #ffd2d2; }
.slip-result { padding: 12px; border: 1px solid #8ed7a8; border-radius: 8px; }
.slip-processing { border-color: #edb848; background: rgba(237,184,72,.12); }
.deposit-transfer .btn-link { color: #fff; text-decoration: underline; }
</style>
